// redirectSafety.js
//
// Pure, dependency-free redirect-safety helpers shared by the login return-routing.
// Extracted so they can be unit-tested without importing the auth/axios chain.
//
// safeExternalRedirect: full-page redirects are permitted ONLY back to VISINVOKE-owned
// https hosts (e.g. the desktop authorize bridge). Anything else -> null (fall through
// to internal SPA navigation), so it can never be an open redirect to an untrusted site.
//
// The ONE exception is the loopback desktop bridge used by a local, non-production
// stack: when the portal is ITSELF being served from 127.0.0.1/localhost, an http
// redirect back to a loopback host is permitted. A deployed portal is never served
// from loopback, so this branch is unreachable in production and the https allowlist
// above is the only rule that can ever apply there.
//
// safeInternalPath: internal SPA redirects must be a single-slash same-origin path;
// protocol-relative '//host', backslash tricks and absolute URLs are rejected.

export const REDIRECT_HOST_ALLOWLIST = ['api.visinvoke.com', 'staging-api.visinvoke.com', 'app.visinvoke.com', 'visinvoke.com', 'www.visinvoke.com'];

export const LOOPBACK_HOSTS = ['127.0.0.1', 'localhost', '[::1]', '::1'];

function isLoopbackHost(hostname) {
  return LOOPBACK_HOSTS.includes(String(hostname || '').toLowerCase());
}

export function safeExternalRedirect(raw, currentOrigin) {
  if (!raw) return null;
  let u;
  try { u = new URL(raw); } catch { return null; }          // relative/internal path -> use navigate()

  // Local, non-production stack only: the portal must itself be on loopback for a
  // loopback http bridge to be honoured. Resolved from the caller's own origin so the
  // helper stays pure and testable.
  const here = currentOrigin
    || (typeof window !== 'undefined' && window.location ? window.location.origin : '');
  let hereHost = '';
  try { hereHost = here ? new URL(here).hostname : ''; } catch { hereHost = ''; }
  if (isLoopbackHost(hereHost) && u.protocol === 'http:' && isLoopbackHost(u.hostname)) {
    return u.toString();
  }

  if (u.protocol !== 'https:') return null;
  if (!REDIRECT_HOST_ALLOWLIST.includes(u.hostname.toLowerCase())) return null;
  return u.toString();
}

export function safeInternalPath(raw) {
  if (!raw || typeof raw !== 'string') return null;
  if (raw[0] !== '/') return null;
  if (raw[1] === '/' || raw[1] === '\\') return null;
  return raw;
}
